export function openCart(getComponentExists, openOverlay) {
  if (typeof getComponentExists !== 'function' || typeof openOverlay !== 'function') {
    return
  }
  if (getComponentExists('cart-drawer')) {
    openOverlay({
      component: 'cart-drawer',
      ignoreDismissed: true,
      namespace: 'cartDrawer',
    })
  } else if (getComponentExists('cart-notice')) {
    openOverlay({
      namespace: 'cartNotice',
      ignoreDismissed: true,
    })
  }
}

// Normalize metafield booleans from Shopify responses.
function normalizeMetafieldBoolean(value) {
  return value === true || value === 'true' || value === 'True'
}

// Strip the backorder prefix before showing the message.
function cleanBackorderMessage(value = '') {
  return String(value || '').replace(/^backordered:\s*/i, '').trim()
}

// Compare two property objects by key and value.
function haveSameProperties(current = {}, next = {}) {
  const currentKeys = Object.keys(current)
  const nextKeys = Object.keys(next)

  if (currentKeys.length !== nextKeys.length) {
    return false
  }

  return currentKeys.every((key) => current[key] === next[key])
}

// Build the managed cart property update payload.
export function buildManagedPropertyUpdate(
  item,
  extraItem = {},
  { backorderKey = 'Backordered', engravingEddKey = 'Estimated Ship Date' } = {},
) {
  const currentProperties = { ...(item.properties || {}) }
  const nextProperties = { ...currentProperties }

  const isBackordered = normalizeMetafieldBoolean(extraItem.is_backordered)

  if (isBackordered) {
    nextProperties._backorder = 'true'
    delete nextProperties._edd

    const nextBackorderMessage = cleanBackorderMessage(extraItem.backorder_message)
    if (nextBackorderMessage) {
      nextProperties[backorderKey] = nextBackorderMessage
    } else {
      delete nextProperties[backorderKey]
    }
  } else {
    delete nextProperties._backorder
    delete nextProperties[backorderKey]
  }

  if (
    Object.prototype.hasOwnProperty.call(currentProperties, engravingEddKey) &&
    extraItem.engraving_edd
  ) {
    nextProperties[engravingEddKey] = extraItem.engraving_edd
  }

  const finalProperties = Object.keys(nextProperties).length
    ? nextProperties
    : { _edd: '' }

  if (haveSameProperties(currentProperties, finalProperties)) {
    return null
  }

  return {
    id: item.key,
    quantity: item.quantity,
    properties: finalProperties,
  }
}

// Parse the custom.pickup_availability metafield, which arrives as an object
// from Liquid but may be a JSON string from other sources.
function parsePickupAvailability(value) {
  if (!value) {
    return null
  }

  if (typeof value === 'object') {
    return Array.isArray(value) ? null : value
  }

  if (typeof value !== 'string') {
    return null
  }

  try {
    const parsed = JSON.parse(value)
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? parsed
      : null
  } catch {
    return null
  }
}

// A line can be collected when it isn't backordered and its availability
// metafield marks at least one store as in stock.
function canBeCollected(extraItem) {
  if (normalizeMetafieldBoolean(extraItem?.is_backordered)) {
    return false
  }

  const availability = parsePickupAvailability(extraItem?.pickup_availability)

  if (!availability) {
    return false
  }

  return Object.values(availability).some((inStock) => inStock === true)
}

/**
 * Whether anything in the cart can be collected from a store.
 * - Drives the `_pickup_available` cart attribute, which Checkout Blocks uses
 *   to hide the local pickup method entirely when it's 'false'.
 * - Shopify's own pickup availability can't be used: "continue selling when
 *   out of stock" is enabled on most styles for backorders, which makes every
 *   variant look collectable at every store.
 * - Anything unconfirmed (missing or malformed metafield) counts as not
 *   collectable, so pickup is hidden rather than wrongly offered.
 * @param {Array} extra - Line data from the cart.resource template.
 * @returns {Boolean}
 */
export function isCartPickupAvailable(extra = []) {
  if (!Array.isArray(extra) || !extra.length) {
    return false
  }

  return extra.some(canBeCollected)
}
